import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { keepAppLayersOpen } from 'features/tours/guideEvents'
import styled from 'styled-components'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded'
import { otherLayerOpen } from './TourSpotlight'

export const BAR_HEIGHT = 72

export const barGap = () => (typeof window !== 'undefined' && window.innerWidth <= 640 ? 8 : 16)

const MISSING_TEXT = "We can't find this on your screen. This can happen if your account doesn't have this option."

const WorkflowBar = ({ title, stepNumber, stepCount, missing, missingText, optional = false, status, canBack, canSkip, onBack, onSkip, onStop, onKeepGoing, onMeasure, startConfirming = false, raised = true, docked = false, escapeToStop = false }) => {
  const [confirming, setConfirming] = useState(!!startConfirming)
  const barRef = useRef(null)
  const onMeasureRef = useRef(onMeasure)
  onMeasureRef.current = onMeasure

  useLayoutEffect(() => {
    const el = barRef.current
    if (!el) return undefined
    const measure = () => {
      if (onMeasureRef.current) onMeasureRef.current(el.getBoundingClientRect().height)
    }
    measure()
    window.addEventListener('resize', measure)
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null
    if (observer) observer.observe(el)
    return () => {
      window.removeEventListener('resize', measure)
      if (observer) observer.disconnect()
    }
  }, [])

  useEffect(() => {
    if (!escapeToStop) return undefined
    const onKey = e => {
      if (e.key !== 'Escape' || e.defaultPrevented || otherLayerOpen()) return
      setConfirming(true)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [escapeToStop])

  const isLast = stepCount > 0 && stepNumber >= stepCount

  const keepGoing = () => {
    setConfirming(false)
    if (onKeepGoing) onKeepGoing()
  }

  return createPortal(
    <Bar
      ref={barRef}
      {...keepAppLayersOpen}
      role='region'
      aria-label={`Guide: ${title}`}
      $raised={raised}
      $docked={docked}
      $gap={barGap()}
    >
      <Info>
        <Title>{title}</Title>
        <Count aria-live='polite'>{stepCount > 0 ? `Step ${Math.min(stepNumber, stepCount)} of ${stepCount}` : 'Getting ready'}</Count>
        {status && !missing && <Status role='status'>{status}</Status>}
        {missing && optional && (
          <Note role='status'>
            <InfoOutlinedIcon aria-hidden='true' fontSize='small' />
            <span>{missingText || MISSING_TEXT}</span>
          </Note>
        )}
        {missing && !optional && (
          <Missing role='alert'>
            <ErrorOutlineRoundedIcon aria-hidden='true' fontSize='small' />
            <span>{missingText || MISSING_TEXT}</span>
          </Missing>
        )}
      </Info>
      {confirming ? (
        <Actions>
          <Question>Stop this guide?</Question>
          <Secondary type='button' onClick={keepGoing}>Keep going</Secondary>
          <Primary type='button' onClick={onStop}>Yes, stop the guide</Primary>
        </Actions>
      ) : (
        <Actions>
          {canBack && <Secondary type='button' onClick={onBack}>Back</Secondary>}
          {canSkip && <Secondary type='button' onClick={onSkip}>{isLast ? 'Finish guide' : 'Skip this step'}</Secondary>}
          <Secondary type='button' onClick={() => setConfirming(true)}>Stop guide</Secondary>
        </Actions>
      )}
    </Bar>,
    document.body
  )
}

export default WorkflowBar

const Bar = styled.div`
  position: fixed;
  left: 50%;
  bottom: 16px;
  transform: translateX(-50%);
  z-index: ${({ $raised }) => ($raised ? 10000001 : 100050)};
  width: min(760px, calc(100vw - 32px));
  min-height: ${BAR_HEIGHT - 16}px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding: 10px 14px;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 4px 14px rgba(12, 18, 41, 0.12);

  @media (max-width: 640px) {
    bottom: calc(8px + env(safe-area-inset-bottom, 0px));
  }

  ${({ $docked, $gap }) => $docked && `
    && {
      top: calc(${$gap}px + env(safe-area-inset-top, 0px));
      bottom: auto;
    }
  `}
`

const Info = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`

const Title = styled.strong`
  font-size: 15px;
  color: #0C1229;
`

const Count = styled.span`
  font-size: 15px;
  color: #475467;
`

const Status = styled.span`
  font-size: 15px;
  color: #0C1229;
`

const Missing = styled.span`
  display: flex;
  align-items: flex-start;
  gap: 6px;
  font-size: 15px;
  color: #b00020;
  & > svg { flex: none; margin-top: 1px; }
`

const Note = styled(Missing)`
  color: #0C1229;
  & > svg { color: #475467; }
`

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`

const Question = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: #0C1229;
`

const Secondary = styled.button`
  min-height: 40px;
  padding: 0 14px;
  border: 1px solid #e5e7eb;
  border-radius: 22px;
  background: #fff;
  color: #0C1229;
  font: inherit;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  &:hover { border-color: #33becc; }
  &:focus-visible { outline: 2px solid #33becc; outline-offset: 2px; }
`

const Primary = styled(Secondary)`
  border-color: #0C1229;
  background: #0C1229;
  color: #fff;
  &:hover { border-color: #0C1229; }
`
